import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePassword, formatRoleName, formatPermissionName } from 'utils'
import { showAlert } from 'services/alerts.service'
import { ROLE_IDS } from 'constants/roles'
import ModalComponent from 'components/common/ModalComponent'
import LabeledInputComponent from 'components/common/LabeledInputComponent'
import LabeledSelectComponent from 'components/common/LabeledSelectComponent'
import FormFooter from 'components/System/FormFooter'
import PasswordField from 'components/System/PasswordField'
import { systemRequest, getRequestErrorMessage, getMissingFields } from 'components/System/systemRequest'

const toFormData = user => user
  ? {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      roleId: user.roleId ? String(user.roleId) : '',
      permissions: user.permissions || []
    }
  : {
      firstName: '',
      lastName: '',
      email: '',
      password: generatePassword(12),
      roleId: '',
      permissions: []
    }

// Create or edit a user of a tenant. The owner keeps its role and has full
// access regardless of permissions, so both are locked for it.
const TenantUserFormModal = ({ isOpen, farmId, user, roles, permissionsList, onClose, onSaved }) => {
  const { t } = useTranslation()
  const [missingFields, setMissingFields] = useState([])
  const [saving, setSaving] = useState(false)
  const isEditing = !!user
  const isOwner = !!user?.isOwner
  const initialData = useMemo(() => toFormData(user), [user, isOpen])
  const assignableRoles = roles.filter(role => Number(role.roleId) !== ROLE_IDS.SYSTEM_ADMIN)

  useEffect(() => {
    if (isOpen) setMissingFields([])
  }, [isOpen])

  const handleSave = async formData => {
    const missing = getMissingFields(formData, isEditing ? ['email', 'roleId'] : ['email', 'roleId', 'password'])
    setMissingFields(missing)
    if (missing.length) {
      showAlert({ message: t('system.tenants.form.missingFields'), icon: 'warning' })
      return
    }

    const payload = {
      firstName: formData.firstName?.trim() || null,
      lastName: formData.lastName?.trim() || null,
      email: formData.email.trim(),
      roleId: Number(formData.roleId),
      permissions: formData.permissions || [],
      ...(!isEditing && { password: formData.password })
    }

    setSaving(true)
    try {
      await systemRequest(
        isEditing ? 'PUT' : 'POST',
        isEditing ? `/system/tenants/${farmId}/users/${user.userId}` : `/system/tenants/${farmId}/users`,
        payload
      )
      showAlert({ message: t(isEditing ? 'system.tenants.userForm.updated' : 'system.tenants.userForm.created'), icon: 'success' })
      onSaved()
    } catch (error) {
      showAlert({ message: getRequestErrorMessage(error, t('system.tenants.form.saveError')), icon: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const missingSet = new Set(missingFields)

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('system.tenants.userForm.editTitle', { email: user.email }) : t('system.tenants.userForm.createTitle')}
      initialData={initialData}
      onSave={handleSave}
      maxWidth='3xl'
      customFooter={<FormFooter onClose={onClose} saving={saving} />}
    >
      {({ formData, handleChange }) => (
        <div className='flex flex-col gap-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <LabeledInputComponent
              label={t('system.tenants.form.firstName')}
              name='firstName'
              type='text'
              value={formData.firstName || ''}
              onChange={handleChange}
            />
            <LabeledInputComponent
              label={t('system.tenants.form.lastName')}
              name='lastName'
              type='text'
              value={formData.lastName || ''}
              onChange={handleChange}
            />
            <LabeledInputComponent
              label={t('system.tenants.form.email')}
              name='email'
              type='email'
              value={formData.email || ''}
              onChange={handleChange}
              missingFields={missingSet}
              required
            />
            {!isEditing && (
              <PasswordField
                value={formData.password}
                onChange={handleChange}
                isMissing={missingSet.has('password')}
              />
            )}
            <LabeledSelectComponent
              name='roleId'
              label={t('system.tenants.cols.role')}
              value={formData.roleId || ''}
              onChange={handleChange}
              missingFields={missingFields}
              disabled={isOwner}
              required
            >
              {assignableRoles.map(role => (
                <option key={role.roleId} value={String(role.roleId)}>{formatRoleName(role.roleName)}</option>
              ))}
            </LabeledSelectComponent>
          </div>

          {isOwner
            ? <p className='text-sm text-gray-500'>{t('system.tenants.userForm.ownerAccess')}</p>
            : (
              <div>
                <h4 className='text-sm font-medium text-gray-700'>{t('mgmt.userModal.access')}</h4>
                <div className='grid grid-cols-2 md:grid-cols-3 gap-3 py-3'>
                  {permissionsList.map(permission => (
                    <label key={permission.permissionId} className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='checkbox'
                        checked={(formData.permissions || []).includes(permission.permissionId)}
                        onChange={e => {
                          const current = formData.permissions || []
                          const permissions = e.target.checked
                            ? [...current, permission.permissionId]
                            : current.filter(id => id !== permission.permissionId)
                          handleChange({ target: { name: 'permissions', value: permissions } })
                        }}
                        className='w-4 h-4 text-green-600 border-gray-300 rounded'
                      />
                      <span>{formatPermissionName(permission.name)}</span>
                    </label>
                  ))}
                </div>
              </div>
              )}
        </div>
      )}
    </ModalComponent>
  )
}

export default TenantUserFormModal
